import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import LabelAutoComplete from '../components/LabelAutoComplete'
import LabelOnly from '../components/LabelOnly'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  findAllLoadCustGQL,
  findAllSellTOUGQL,
  findLoadDataGQL,
  findOneSellTOUGQL,
} from '../gql/SellPlans.gql'
import MonthLineGraph2 from '../components/graph/MonthLineGraph2'
import TOUHeatMap from '../components/graph/TOUHeatMap'
import {
  findOneBuyTOUGQL,
  findOneKepcoTOUGQL,
  findPVDataGQL,
  getHolidaysGQL,
} from '../gql/PurchasePlans.gql'
import {
  ContentWrapper,
  Body,
  Bottom,
  FirstRow,
  GraphBody,
  GraphWrapper,
  Header,
  Item,
  ItemWrapper,
  Left,
  MonthChip,
  MonthChipText,
  MonthChipWrapper,
  Right,
  Table,
  TableCell,
  TableCell2,
  TableRow,
  Title,
} from './EconomicCapacity.styled'
import {
  ILoadTimeSum,
  ISeasonalPVCharge,
  ITimeDataItem,
} from '../util/algorithm/algoInterface'
import {
  calLoadTimeSum,
  calMonthSum,
  divTimeData,
  mulTimeData,
} from '../util/algorithm/classifyTimeData'
import { calMonthRepPattern } from '../util/algorithm/calRepPattern'
import { seasonMapping } from '../util/SeasonMapping'
import {
  calKepcoMonthlyChargeAsUsage,
  calMonthlyChargeAsUsage,
} from '../util/algorithm/calCharge'
import LabelUnit from '../components/LabelUnit'
import { INIT_KEPCO_TOU_PERIODS } from '../util/contants'
import {
  calSeasonalSum,
  monthSumToSeasonSum,
} from '../util/algorithm/monthToSeason'
import {
  buildPeakDeductionLoadData,
  sumBuyCostGeneration,
} from '../util/algorithm/peakDeductionSellPlan'

const getSellPlanTypeLabel = (sellTOUData?: any) => {
  if (sellTOUData?.planType === 'peakDeduction') {
    return '최대부하 시간대 차감형 단일단가'
  }

  if (sellTOUData?.planType === 'flat' || sellTOUData?.rateOpt === 'flat') {
    return '모든 시간대 비율 차감형 단일단가'
  }

  return '모든 시간대 비율 차감형 TOU'
}

export const EconomicCapacity = () => {
  const [PageData, setPageData] = useState<any>({
    cust: '',
    plans: '',
  })

  const { data: originLoadCusList } = useQuery(findAllLoadCustGQL)
  const [LoadCusList, setLoadCusList] = useState<any>()

  const { data: originSellTOUList } = useQuery(findAllSellTOUGQL)
  const [sellTOUList, setSellTOUList] = useState<any>()

  const [findLoadData] = useLazyQuery(findLoadDataGQL)
  //수용가 데이터
  const [LoadData, setLoadData] = useState<any>()

  const [findOneSellPlanData] = useLazyQuery(findOneSellTOUGQL)
  //판매요금제 데이터
  const [SellTOUData, setSellTOUData] = useState<any>()
  const [findPVData] = useLazyQuery(findPVDataGQL)
  const [findOnePurchasePlanData] = useLazyQuery(findOneBuyTOUGQL)
  const filteredSellTOUList = useMemo(() => {
    if (!sellTOUList) return []
    if (!PageData.cust?._id) return sellTOUList

    return sellTOUList.filter((plan: any) => plan.custId === PageData.cust._id)
  }, [PageData.cust?._id, sellTOUList])

  //공휴일 데이터
  const [holidaysData, setHolidaysData] = useState<any>()
  const [findHolidaysData] = useLazyQuery(getHolidaysGQL)

  // 수용가 한전요금제
  const [kepcoData, setKepcoData] = useState<any>()
  const [findKepcoData] = useLazyQuery(findOneKepcoTOUGQL)

  // 옵션따라 고른 부하 데이터
  const [usingLoadData, setUsingLoadData] = useState<ITimeDataItem[]>([])
  // ESS 사업자에게 사올 부하 데이터
  const [ESSLoadData, setESSLoadData] = useState<ITimeDataItem[]>([])
  // 한전에게 사올 부하 데이터
  const [kepcoLoadData, seKepcoLoadData] = useState<ITimeDataItem[]>([])

  //시간대별 소비량 계산
  const [timeLoad, setTimeLoad] = useState<ILoadTimeSum>()

  // 이전 월별 한전 전기요금
  const [beforeKepcoCharge, setBeforeKepcoCharge] = useState<any>()
  // 이후 월별 한전 전기요금
  const [afterKepcoCharge, setAfterKepcoCharge] = useState<any>()
  // 이후 ESS 사업자 요금
  const [afterESSCharge, setAfterESSCharge] = useState<any>()

  // 이전 월별 한전 전력량
  const [beforeKepcoUsage, setBeforeKepcoUsage] = useState<any>()
  // 이후 월별 한전 전력량
  const [afterKepcoUsage, setAfterKepcoUsage] = useState<any>()
  // 이후 ESS 사업자 전력량
  const [afterESSUsage, setAfterESSUsage] = useState<any>()
  const [buyCost, setBuyCost] = useState<ISeasonalPVCharge>({
    spr: {
      pv: 0,
      charge: 0,
    },
    sum: {
      pv: 0,
      charge: 0,
    },
    fall: {
      pv: 0,
      charge: 0,
    },
    win: {
      pv: 0,
      charge: 0,
    },
  })

  const [selectedMonths, setSelectedMonths] = useState<number[]>([1])
  const [touPeriodsData, setTouPeriodsData] = useState<any>()
  const isPeakDeductionSellPlan = SellTOUData?.planType === 'peakDeduction'
  //공휴일데이터 세팅
  useEffect(() => {
    if (SellTOUData?.holidayYear && SellTOUData?.holidayYear !== '') {
      findHolidaysData({
        variables: {
          val: String(SellTOUData.holidayYear),
        },
      }).then((result: any) => {
        //@ts-ignore
        if (result.data) setHolidaysData(Object.values(result.data)[0].data)
      })
    }
  }, [SellTOUData])

  // 부하 선택
  useEffect(() => {
    if (LoadData && SellTOUData) {
      const tmpData = divTimeData(LoadData, 1000)
      const loadOpt =
        PageData.cust?.loadOpt || SellTOUData?.cust?.loadOpt || 'realTime'

      switch (loadOpt) {
        case 'aver':
          const monthRepPatternAver = calMonthRepPattern(tmpData, 'aver')
          setUsingLoadData(monthRepPatternAver.repData)
          break

        case 'mid':
          const monthRepPatternMid = calMonthRepPattern(tmpData, 'mid')
          setUsingLoadData(monthRepPatternMid.repData)
          break

        case 'mode':
          const monthRepPatternMode = calMonthRepPattern(tmpData, 'mode')
          setUsingLoadData(monthRepPatternMode.repData)
          break

        case 'realTime':
        default:
          setUsingLoadData(tmpData)
          break
      }
    }
  }, [LoadData, PageData.cust?.loadOpt, SellTOUData])

  //소비량 데이터 세팅
  useEffect(() => {
    if (SellTOUData && usingLoadData && holidaysData) {
      setTimeLoad(
        calLoadTimeSum(
          usingLoadData,
          seasonMapping,
          holidaysData,
          SellTOUData.touPeriodResult
        ).loadTimeSumResult
      )
    }
  }, [usingLoadData, holidaysData])

  useEffect(() => {
    if (!SellTOUData?.buyTOUList || SellTOUData.buyTOUList.length === 0) {
      setBuyCost({
        spr: { pv: 0, charge: 0 },
        sum: { pv: 0, charge: 0 },
        fall: { pv: 0, charge: 0 },
        win: { pv: 0, charge: 0 },
      })
      return
    }

    if (!holidaysData || holidaysData.length === 0) {
      return
    }

    const fetchBuyCost = async () => {
      const result: ISeasonalPVCharge = {
        spr: { pv: 0, charge: 0 },
        sum: { pv: 0, charge: 0 },
        fall: { pv: 0, charge: 0 },
        win: { pv: 0, charge: 0 },
      }

      const buyResults = await Promise.all(
        SellTOUData.buyTOUList.map(async (item: any) => {
          const custPromise = await findPVData({
            variables: { val: item?.cust?._id },
          })
          const plansPromise = await findOnePurchasePlanData({
            variables: { val: item?._id },
          })

          return {
            pvdata: custPromise.data ? Object.values(custPromise.data)[0] : '',
            plansdata: plansPromise.data
              ? Object.values(plansPromise.data)[0]
              : '',
          }
        })
      )

      buyResults.forEach((element: any) => {
        if (!element.pvdata || !element.plansdata) return

        const pvdata = divTimeData(element.pvdata, 1000)
        const monthPVSum = calSeasonalSum(pvdata, seasonMapping)

        result.spr.pv += monthPVSum.spr
        result.sum.pv += monthPVSum.sum
        result.fall.pv += monthPVSum.fall
        result.win.pv += monthPVSum.win

        if (
          element.plansdata.chargeUnitResult &&
          element.plansdata.touPeriodResult
        ) {
          const monthlyCharge = calMonthlyChargeAsUsage(
            pvdata,
            element.plansdata.touPeriodResult,
            element.plansdata.chargeUnitResult,
            seasonMapping,
            holidaysData
          )
          const seasonalCharge = monthSumToSeasonSum(
            monthlyCharge,
            seasonMapping
          )

          result.spr.charge += seasonalCharge.spr
          result.sum.charge += seasonalCharge.sum
          result.fall.charge += seasonalCharge.fall
          result.win.charge += seasonalCharge.win
        }
      })

      setBuyCost(result)
    }

    fetchBuyCost()
  }, [SellTOUData?.buyTOUList, holidaysData])

  // 비율에 따른 부하데이터 세팅, 부하합 계산
  useEffect(() => {
    if (usingLoadData && usingLoadData.length !== 0 && SellTOUData) {
      setBeforeKepcoUsage(calMonthSum(usingLoadData))

      if (isPeakDeductionSellPlan) {
        if (!holidaysData || !touPeriodsData) return

        const peakDeductionLoadData = buildPeakDeductionLoadData({
          loadData: usingLoadData,
          totalPvGeneration: sumBuyCostGeneration(buyCost),
          seasonMapping,
          holidayData: holidaysData,
          touPeriods: touPeriodsData,
        })

        setESSLoadData(peakDeductionLoadData.essLoadData)
        seKepcoLoadData(peakDeductionLoadData.kepcoLoadData)

        setAfterKepcoUsage(calMonthSum(peakDeductionLoadData.kepcoLoadData))
        setAfterESSUsage(calMonthSum(peakDeductionLoadData.essLoadData))
        return
      }

      const essRate = SellTOUData.essPowerRate / 100
      const kepcoRate = 1 - essRate

      const essTimeData = mulTimeData(usingLoadData, essRate)
      const kepcoTimeData = mulTimeData(usingLoadData, kepcoRate)

      setESSLoadData(essTimeData)
      seKepcoLoadData(kepcoTimeData)

      setAfterKepcoUsage(calMonthSum(kepcoTimeData))
      setAfterESSUsage(calMonthSum(essTimeData))
    }
  }, [
    usingLoadData,
    SellTOUData,
    isPeakDeductionSellPlan,
    holidaysData,
    touPeriodsData,
    buyCost,
  ])

  //한전 요금제 불러오기
  useEffect(() => {
    if (SellTOUData) {
      if (SellTOUData.touVer && SellTOUData.touVer !== '') {
        findKepcoData({
          variables: {
            val: SellTOUData?.touVer,
          },
        }).then((result: any) => {
          if (!result.data) return
          const kepcoTOU = Object.values(result.data)[0] as any
          setTouPeriodsData(
            kepcoTOU.kepcoTOUPeriod?.touPeriods ||
              SellTOUData.touPeriodResult ||
              INIT_KEPCO_TOU_PERIODS
          )
          if (SellTOUData?.cust?.KEPCOPlanType?.name) {
            setKepcoData(
              //@ts-ignore
              kepcoTOU.data.filter(
                (v: any) => v.name === SellTOUData?.cust?.KEPCOPlanType?.name
              )[0]
            )
          }
        })
      }
    }
  }, [SellTOUData])

  // 한전에 내던 기존 요금 계산하기
  useEffect(() => {
    if (
      usingLoadData.length !== 0 &&
      kepcoData &&
      holidaysData &&
      holidaysData.length !== 0 &&
      SellTOUData &&
      touPeriodsData
    ) {
      const monthlyResult = calKepcoMonthlyChargeAsUsage(
        usingLoadData,
        touPeriodsData,
        kepcoData.chargeUnit,
        seasonMapping,
        holidaysData,
        kepcoData.defaultChargeUnit,
        SellTOUData.cust.prevKEPCOContractedPower,
        SellTOUData.environmentalUnit
      )

      setBeforeKepcoCharge(monthlyResult)
    }
  }, [
    usingLoadData,
    kepcoData,
    holidaysData,
    touPeriodsData,
    SellTOUData?.environmentalUnit,
  ])

  // 한전에 낼 나중 요금 계산하기
  useEffect(() => {
    if (
      usingLoadData.length !== 0 &&
      kepcoLoadData.length !== 0 &&
      ESSLoadData.length !== 0 &&
      kepcoData &&
      holidaysData &&
      holidaysData.length !== 0 &&
      SellTOUData &&
      touPeriodsData
    ) {
      const afterContractedPower = isPeakDeductionSellPlan
        ? SellTOUData.cust.prevKEPCOContractedPower
        : SellTOUData.afterKEPCOContractedPower
      const monthlyResult = calKepcoMonthlyChargeAsUsage(
        kepcoLoadData,
        touPeriodsData,
        kepcoData.chargeUnit,
        seasonMapping,
        holidaysData,
        kepcoData.defaultChargeUnit,
        afterContractedPower,
        SellTOUData.environmentalUnit
      )
      setAfterKepcoCharge(monthlyResult)

      setAfterESSCharge(
        calMonthlyChargeAsUsage(
          ESSLoadData,
          touPeriodsData,
          SellTOUData.chargeUnitResult,
          seasonMapping,
          holidaysData
        )
      )
    }
  }, [
    usingLoadData,
    kepcoData,
    holidaysData,
    touPeriodsData,
    kepcoLoadData,
    ESSLoadData,
    SellTOUData,
    isPeakDeductionSellPlan,
  ])

  useEffect(() => {
    if (originLoadCusList) setLoadCusList(Object.values(originLoadCusList)[0])
  }, [originLoadCusList])

  useEffect(() => {
    if (originSellTOUList) setSellTOUList(Object.values(originSellTOUList)[0])
  }, [originSellTOUList])

  useEffect(() => {
    if (!PageData.cust?._id || !PageData.plans?._id) return
    if (PageData.plans.custId === PageData.cust._id) return

    setPageData((prev: any) => ({
      ...prev,
      plans: '',
    }))
    setSellTOUData(undefined)
    setLoadData(undefined)
  }, [PageData.cust?._id, PageData.plans])

  useEffect(() => {
    const custId = SellTOUData?.cust?._id || PageData.cust?._id
    if (!custId) return
    findLoadData({
      variables: {
        val: custId,
      },
    }).then((result: any) => {
      if (result.data) setLoadData(Object.values(result.data)[0])
    })
  }, [PageData.cust?._id, SellTOUData?.cust?._id])

  useEffect(() => {
    if (!PageData.plans?._id) return
    findOneSellPlanData({
      variables: {
        val: PageData?.plans?._id,
      },
    }).then((result: any) => {
      if (result.data) {
        const planData: any = Object.values(result.data)[0]
        setSellTOUData(planData)
        const selectedCustomer = LoadCusList?.find(
          (cust: any) => cust._id === planData?.cust?._id
        )
        if (selectedCustomer) {
          setPageData((prev: any) => ({
            ...prev,
            cust: selectedCustomer,
          }))
        }
      }
    })
  }, [LoadCusList, PageData.plans?._id])

  const toggleMonth = (index: number) => {
    if (selectedMonths.includes(index)) {
      setSelectedMonths(selectedMonths.filter((month) => month !== index))
    } else {
      setSelectedMonths([index])
    }
  }
  const monthNumbers = Array.from({ length: 12 }, (_, index) => index + 1)
  const isFlatSellPlan =
    SellTOUData?.planType === 'flat' || SellTOUData?.rateOpt === 'flat'

  return (
    <ContentWrapper>
      <Header>
        <Title>고객 경제성 분석 - 수용가</Title>
      </Header>
      <Body>
        <Left>
          <FirstRow>
            <LabelAutoComplete
              label="수용가"
              options={LoadCusList}
              value={PageData.cust}
              setData={setPageData}
              fieldName="cust"
            />
            <LabelAutoComplete
              label="요금제"
              options={filteredSellTOUList}
              value={PageData.plans}
              setData={setPageData}
              fieldName="plans"
            />
          </FirstRow>
          <ItemWrapper>
            <LabelOnly label="고객 요금제" />
            <Item>
              {SellTOUData ? (
                <PlanTypeText>{getSellPlanTypeLabel(SellTOUData)}</PlanTypeText>
              ) : null}
              {isFlatSellPlan ? null : (
                <GraphWrapper>
                  <GraphBody>
                    <TOUHeatMap
                      width="100%"
                      height="130px"
                      title="봄"
                      data={touPeriodsData?.spr}
                    />
                    <TOUHeatMap
                      width="100%"
                      height="130px"
                      title="여름"
                      data={touPeriodsData?.sum}
                    />
                    <TOUHeatMap
                      width="100%"
                      height="130px"
                      title="가을"
                      data={touPeriodsData?.fall}
                    />
                    <TOUHeatMap
                      width="100%"
                      height="130px"
                      title="겨울"
                      data={touPeriodsData?.win}
                      showLegend
                    />
                  </GraphBody>
                </GraphWrapper>
              )}
              <Table>
                <TableRow>
                  <TableCell style={{ borderTopLeftRadius: '4px' }}></TableCell>
                  <TableCell>봄</TableCell>
                  <TableCell>여름</TableCell>
                  <TableCell>가을</TableCell>
                  <TableCell style={{ borderTopRightRadius: '4px' }}>
                    겨울
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>경부하</TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.spr.min
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.sum.min
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.fall.min
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.win.min
                    ).toLocaleString() || 0}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>중간부하</TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.spr.mid
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.sum.mid
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.fall.mid
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.win.mid
                    ).toLocaleString() || 0}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell style={{ borderBottomLeftRadius: '4px' }}>
                    최대부하
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.spr.max
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.sum.max
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white>
                    {Number(
                      SellTOUData?.chargeUnitResult?.fall.max
                    ).toLocaleString() || 0}
                  </TableCell>
                  <TableCell white style={{ borderBottomRightRadius: '4px' }}>
                    {Number(
                      SellTOUData?.chargeUnitResult?.win.max
                    ).toLocaleString() || 0}
                  </TableCell>
                </TableRow>
              </Table>
            </Item>
          </ItemWrapper>
        </Left>
        <Right>
          <ItemWrapper>
            <LabelOnly label="수요 패턴" />
            <Item>
              <GraphWrapper>
                <MonthChipWrapper>
                  {Array.from({ length: 12 }, (_, index) => (
                    <MonthChip
                      key={index + 1}
                      selected={selectedMonths.includes(index + 1)}
                      onClick={() => toggleMonth(index + 1)}
                    >
                      <MonthChipText>{`${index + 1}월`}</MonthChipText>
                    </MonthChip>
                  ))}
                </MonthChipWrapper>
                <GraphBody>
                  <MonthLineGraph2
                    month={selectedMonths[0]}
                    data={usingLoadData}
                    width="100%"
                    height="300px"
                    selectedChartDataType="realTime"
                  />
                </GraphBody>
              </GraphWrapper>
            </Item>
          </ItemWrapper>
          <ItemWrapper>
            <LabelUnit label="소비량" unit="단위: kWh" />{' '}
            {/* 단위 수정 권장 (원 -> kWh) */}
            <Item>
              <Table>
                <TableRow>
                  <TableCell style={{ borderTopLeftRadius: '4px' }}>
                    구분
                  </TableCell>
                  <TableCell>최저가격 시간대</TableCell>
                  <TableCell>중간가격 시간대</TableCell>
                  <TableCell>최대부하 시간대</TableCell>
                  <TableCell style={{ borderTopRightRadius: '4px' }}>
                    월간 소비량
                  </TableCell>
                </TableRow>
                {monthNumbers.map((month) => (
                  <TableRow key={month}>
                    <TableCell white>{month}월</TableCell>
                    <TableCell white>
                      {Number(
                        timeLoad?.[month]?.min?.toFixed(2)
                      ).toLocaleString() || 0}
                    </TableCell>
                    <TableCell white>
                      {Number(
                        timeLoad?.[month]?.mid?.toFixed(2)
                      ).toLocaleString() || 0}
                    </TableCell>
                    <TableCell white>
                      {Number(
                        timeLoad?.[month]?.max?.toFixed(2)
                      ).toLocaleString() || 0}
                    </TableCell>
                    <TableCell white>
                      {timeLoad
                        ? Number(
                            (
                              (timeLoad[month]?.min || 0) +
                              (timeLoad[month]?.mid || 0) +
                              (timeLoad[month]?.max || 0)
                            ).toFixed(2)
                          ).toLocaleString()
                        : 0}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell style={{ borderBottomLeftRadius: '4px' }}>
                    총합계
                  </TableCell>
                  <TableCell white>
                    {timeLoad
                      ? Number(
                          Object.values(timeLoad)
                            .reduce(
                              (acc: number, curr: any) => acc + curr.min,
                              0
                            )
                            .toFixed(2)
                        ).toLocaleString()
                      : 0}
                  </TableCell>
                  <TableCell white>
                    {timeLoad
                      ? Number(
                          Object.values(timeLoad)
                            .reduce(
                              (acc: number, curr: any) => acc + curr.mid,
                              0
                            )
                            .toFixed(2)
                        ).toLocaleString()
                      : 0}
                  </TableCell>
                  <TableCell white>
                    {timeLoad
                      ? Number(
                          Object.values(timeLoad)
                            .reduce(
                              (acc: number, curr: any) => acc + curr.max,
                              0
                            )
                            .toFixed(2)
                        ).toLocaleString()
                      : 0}
                  </TableCell>
                  <TableCell white style={{ borderBottomRightRadius: '4px' }}>
                    {timeLoad
                      ? Number(
                          Object.values(timeLoad)
                            .reduce(
                              (acc: number, curr: any) =>
                                acc + curr.min + curr.mid + curr.max,
                              0
                            )
                            .toFixed(2)
                        ).toLocaleString()
                      : 0}
                  </TableCell>
                </TableRow>
              </Table>
            </Item>
          </ItemWrapper>
        </Right>
      </Body>
      <Bottom>
        <LabelUnit label="요금 상세보기" unit="단위: 원" />
        <ItemWrapper>
          <Item>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
              }}
            >
              <span
                style={{
                  fontFamily: 'SUIT',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginRight: '10px',
                }}
              >
                {isPeakDeductionSellPlan ? '전력 차감 방식' : '전력 구매 비율'}
              </span>
              {isPeakDeductionSellPlan ? (
                <span
                  style={{
                    fontFamily: 'SUIT',
                    fontSize: '13px',
                    fontWeight: '500',
                  }}
                >
                  최대부하 시간대 소비량에서 PV 총 발전량만큼 균등 차감
                </span>
              ) : (
                <Ratio2
                  player1="ESS 사업자"
                  player2="한전"
                  value1={SellTOUData?.essPowerRate || 0}
                />
              )}
            </div>

            {/* 스타일 정의 */}
            {(() => {
              const thStyle = {
                fontFamily: 'SUIT',
                fontSize: '12px',
                fontWeight: 500,
                textAlign: 'center' as const,
                verticalAlign: 'middle' as const,
                padding: '8px',
                border: '0.5px solid #e6e6e6',
                backgroundColor: '#fff', // 기본 배경색
              }
              const headerStyle = { ...thStyle, backgroundColor: '#f4f5f8' } // 헤더 배경색
              const isRE100 = SellTOUData?.cust?.re100Opt === 'achieve'
              const greenPremiumUnit = SellTOUData?.greenPremiumUnit ?? 0
              const getMonthlyValue = (data: any, month: number) =>
                Number(data?.[month] ?? 0)
              const sumValues = (data: any) =>
                data
                  ? Object.values(data).reduce(
                      (acc: number, curr: any) => acc + Number(curr),
                      0
                    )
                  : 0
              const formatWon = (value: number) =>
                Number(value.toFixed(0)).toLocaleString()
              const formatKwh = (value: number) =>
                Number(value.toFixed(2)).toLocaleString()
              const getBeforeGreenPremium = (month: number) =>
                isRE100
                  ? getMonthlyValue(beforeKepcoUsage, month) * greenPremiumUnit
                  : 0
              const getAfterGreenPremium = (month: number) =>
                isRE100
                  ? getMonthlyValue(afterKepcoUsage, month) * greenPremiumUnit
                  : 0
              const getBeforeTotalCharge = (month: number) =>
                getMonthlyValue(beforeKepcoCharge, month) +
                getBeforeGreenPremium(month)
              const getAfterTotalCharge = (month: number) =>
                getMonthlyValue(afterKepcoCharge, month) +
                getMonthlyValue(afterESSCharge, month) +
                getAfterGreenPremium(month)
              const beforeGreenPremiumTotal = isRE100
                ? sumValues(beforeKepcoUsage) * greenPremiumUnit
                : 0
              const afterGreenPremiumTotal = isRE100
                ? sumValues(afterKepcoUsage) * greenPremiumUnit
                : 0
              const beforeTotalCharge =
                sumValues(beforeKepcoCharge) + beforeGreenPremiumTotal
              const afterTotalCharge =
                sumValues(afterKepcoCharge) +
                sumValues(afterESSCharge) +
                afterGreenPremiumTotal

              return (
                <table
                  className="table"
                  style={{ borderCollapse: 'collapse', width: '100%' }}
                >
                  <thead>
                    <tr>
                      <th rowSpan={3} style={headerStyle}>
                        월
                      </th>
                      <th colSpan={3} style={headerStyle}>
                        전력거래량 [kWh]
                      </th>
                      <th colSpan={isRE100 ? 8 : 5} style={headerStyle}>
                        전기요금 [원]
                      </th>
                    </tr>
                    <tr>
                      <th style={headerStyle}>이전</th>
                      <th colSpan={2} style={headerStyle}>
                        이후
                      </th>
                      <th colSpan={isRE100 ? 3 : 1} style={headerStyle}>
                        이전
                      </th>
                      <th colSpan={isRE100 ? 4 : 3} style={headerStyle}>
                        이후
                      </th>
                      <th rowSpan={2} style={headerStyle}>
                        절감된
                        <br />
                        전력 및 RE100 달성
                      </th>
                    </tr>
                    <tr>
                      <th style={headerStyle}>한전</th>
                      <th style={headerStyle}>한전</th>
                      <th style={headerStyle}>ESS 사업자</th>
                      <th style={headerStyle}>한전</th>
                      {isRE100 ? (
                        <>
                          <th style={headerStyle}>녹색프리미엄</th>
                          <th style={headerStyle}>총</th>
                        </>
                      ) : null}
                      <th style={headerStyle}>한전</th>
                      <th style={headerStyle}>ESS 사업자</th>
                      {isRE100 ? (
                        <th style={headerStyle}>
                          잔여
                          <br />
                          녹색프리미엄
                        </th>
                      ) : null}
                      <th style={headerStyle}>총</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthNumbers.map((month) => (
                      <tr key={month}>
                        <th style={thStyle}>{month}월</th>
                        <th style={thStyle}>
                          {formatKwh(getMonthlyValue(beforeKepcoUsage, month))}
                        </th>
                        <th style={thStyle}>
                          {formatKwh(getMonthlyValue(afterKepcoUsage, month))}
                        </th>
                        <th style={thStyle}>
                          {formatKwh(getMonthlyValue(afterESSUsage, month))}
                        </th>
                        <th style={thStyle}>
                          {formatWon(getMonthlyValue(beforeKepcoCharge, month))}
                        </th>
                        {isRE100 ? (
                          <>
                            <th style={thStyle}>
                              {formatWon(getBeforeGreenPremium(month))}
                            </th>
                            <th style={thStyle}>
                              {formatWon(getBeforeTotalCharge(month))}
                            </th>
                          </>
                        ) : null}
                        <th style={thStyle}>
                          {formatWon(getMonthlyValue(afterKepcoCharge, month))}
                        </th>
                        <th style={thStyle}>
                          {formatWon(getMonthlyValue(afterESSCharge, month))}
                        </th>
                        {isRE100 ? (
                          <th style={thStyle}>
                            {formatWon(getAfterGreenPremium(month))}
                          </th>
                        ) : null}
                        <th style={thStyle}>
                          {formatWon(getAfterTotalCharge(month))}
                        </th>
                        <th style={thStyle}>
                          {formatWon(
                            getBeforeTotalCharge(month) -
                              getAfterTotalCharge(month)
                          )}
                        </th>
                      </tr>
                    ))}

                    {/* [추가] 총합계 행 */}
                    {/* [수정] 총합계 행 (타입 에러 수정됨) */}
                    <tr>
                      <th style={{ ...headerStyle, fontWeight: 700 }}>
                        총합계
                      </th>
                      <th style={thStyle}>
                        {formatKwh(sumValues(beforeKepcoUsage))}
                      </th>
                      <th style={thStyle}>
                        {formatKwh(sumValues(afterKepcoUsage))}
                      </th>
                      <th style={thStyle}>
                        {formatKwh(sumValues(afterESSUsage))}
                      </th>
                      <th style={thStyle}>
                        {formatWon(sumValues(beforeKepcoCharge))}
                      </th>
                      {isRE100 ? (
                        <>
                          <th style={thStyle}>
                            {formatWon(beforeGreenPremiumTotal)}
                          </th>
                          <th style={thStyle}>
                            {formatWon(beforeTotalCharge)}
                          </th>
                        </>
                      ) : null}
                      <th style={thStyle}>
                        {formatWon(sumValues(afterKepcoCharge))}
                      </th>
                      <th style={thStyle}>
                        {formatWon(sumValues(afterESSCharge))}
                      </th>
                      {isRE100 ? (
                        <th style={thStyle}>
                          {formatWon(afterGreenPremiumTotal)}
                        </th>
                      ) : null}
                      <th style={thStyle}>{formatWon(afterTotalCharge)}</th>
                      <th style={thStyle}>
                        {formatWon(beforeTotalCharge - afterTotalCharge)}
                      </th>
                    </tr>
                  </tbody>
                </table>
              )
            })()}
          </Item>
        </ItemWrapper>
      </Bottom>
    </ContentWrapper>
  )
}

const Ratio2 = (props: RatioProps) => {
  const { player1 = '-', player2 = '-', value1 } = props
  const [newPlayer2, setNewPlayer2] = useState<JSX.Element | null>(null)
  const value2 = 100 - Number(value1)

  useEffect(() => {
    const lines = player2?.split('\n')
    const elements = lines?.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
    setNewPlayer2(<StyledParagraph>{elements}</StyledParagraph>)
  }, [player2])

  return (
    <Frame>
      <Box>
        <PlayerBox>{player1}</PlayerBox>
        <ValueBox>
          <InputFrame>{value1}</InputFrame>
          <Line />
        </ValueBox>
      </Box>
      <Colon>:</Colon>
      <Box>
        <PlayerBox>{newPlayer2}</PlayerBox>
        <ValueBox>
          <InputFrame>{value2}</InputFrame>
          <Line />
        </ValueBox>
      </Box>
    </Frame>
  )
}

interface RatioProps {
  player1?: string
  player2?: string
  value1?: number
  value2?: number
}

const Frame = styled.div`
  display: flex;
  padding-left: 10px;
  align-items: center;
  gap: 8px;
`

const Box = styled.div`
  display: flex;
  width: 72px;
  height: 64px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  background: var(--green10, #f4fcfa);
`
const PlayerBox = styled.div`
  color: var(--green100, #57c9a7);
  font-family: SUIT;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-align: center;
`

const ValueBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`

const Line = styled.div`
  width: 32px;
  height: 1px;
  background: var(--gray50, #b3b3b3);
`
const Colon = styled.div`
  color: var(--gray90, #333);
  font-family: SUIT;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
`

const InputFrame = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const PlanTypeText = styled.div`
  color: var(--gray90, #333);
  font-family: SUIT;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
`

const StyledParagraph = styled.p`
  margin: 0; /* 마진 제거 */
  text-align: center;
`
